import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomButton from "../Common/CustomButton";
import { TagsInputUniqueKeys } from "../Common/TagInputsUniqueKeys";
import useFetch from "../Common/useFetch";
import { CN_Status_Type } from "../VendorNew/VendorSideDashboard";
import { IYesNo } from "./AnchorDashboard";

export default function AnchorDashboardFilter({
  open,
  onClose,
  setAppliedFilters,
  appliedFilters,
  useCase,
}: {
  open: boolean;
  onClose: () => void;
  setAppliedFilters: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  appliedFilters: Record<string, string[]>;
  useCase: "dailyPayments" | "previousPayments" | "anchorSidePayments";
}) {
  const [vendorNames, setVendorNames] = useState<string[]>(appliedFilters.vendorName || []);
  const [PANNumbers, setPANNumbers] = useState<string[]>(appliedFilters.panNumber || []);
  const [GRNNumbers, setGRNNumbers] = useState<string[]>(appliedFilters.grnNumber || []);
  const [invoiceNumbers, setInvoiceNumbers] = useState<string[]>(appliedFilters.invoiceNumber || []);
  const [cnStatuses, setCNStatuses] = useState<string[]>(appliedFilters.cnStatus || []);
  const [dnAdjusted, setDNAdjusted] = useState<string[]>(appliedFilters.dnAdjusted || []);
  const [nakadCnReceived, setNakadCnReceived] = useState<string[]>(appliedFilters.nakadCnReceived || []);
  const [anchorCnReceived, setAnchorCnReceived] = useState<string[]>(appliedFilters.anchorCnReceived || []);
  const [warehouseName, setWarehouseName] = useState<string[]>(appliedFilters.warehouseName || []);
  const [earlyPayPass, setEarlyPayPass] = useState<string[]>(appliedFilters.earlyPayPass || []);

  const [fromEarlyPayDate, setFromEarlyPayDate] = useState<string>(
    appliedFilters.earlyPayDate ? appliedFilters.earlyPayDate[0] : null
  );
  const [toEarlyPayDate, setToEarlyPayDate] = useState<string>(
    appliedFilters.earlyPayDate ? appliedFilters.earlyPayDate[1] : null
  );

  const [vendorNameOptions, setVendorNamesOptions] = useState<{ vendorName: string }[]>([]);
  const [PANOptions, setPANOptions] = useState<{ panNumber: string }[]>([]);
  const [GRNOptions, setGRNOptions] = useState<{ grnNumber: string }[]>([]);
  const [invoiceOptions, setInvoiceOptions] = useState<{ invoiceNumber: string }[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<{ warehouseName: string }[]>([]);

  const getFilterOptions = async () => {
    const url =
      useCase === "dailyPayments"
        ? "/invoices/early-payment/2/filters/options"
        : "/invoices/previous-payment/2/filters/options";
    await useFetch(url, "GET", {
      failureMessage: "Failed to fetch filter options",
      thenCallBack: (response) => {
        const data = response.data.result;
        setVendorNamesOptions(
          data.vendorName.map((vendor: string) => {
            return { vendorName: vendor };
          }) || []
        );
        setPANOptions(data.pan.map((pan: string) => ({ panNumber: pan })) || []);
        setGRNOptions(data.grnNumber.map((grn: string) => ({ grnNumber: grn })) || []);
        setInvoiceOptions(data.invoiceNumber.map((invoice: string) => ({ invoiceNumber: invoice })) || []);
        setWarehouseOptions(data.warehouseName.map((warehouse: string) => ({ warehouseName: warehouse })) || []);
      },
    });
  };

  useEffect(() => {
    getFilterOptions();
  }, []);

  const handleApplyFilter = () => {
    setAppliedFilters({
      vendorName: vendorNames.filter((v) => v.trim() !== ""),
      panNumber: PANNumbers.filter((v) => v.trim() !== ""),
      grnNumber: GRNNumbers.filter((v) => v.trim() !== ""),
      invoiceNumber: invoiceNumbers.filter((v) => v.trim() !== ""),
      earlyPayDate: [fromEarlyPayDate, toEarlyPayDate],
      cnStatus: cnStatuses.filter((v) => v.trim() !== ""),
      dnAdjusted: dnAdjusted.filter((v) => v.trim() !== ""),
      nakadCnReceived: nakadCnReceived.filter((v) => v.trim() !== ""),
      anchorCnReceived: anchorCnReceived.filter((v) => v.trim() !== ""),
      warehouseName: warehouseName.filter((v) => v.trim() !== ""),
      earlyPayPass: earlyPayPass.filter((v) => v.trim() !== ""),
    });
    onClose();
  };

  const handleClearFilters = () => {
    setAppliedFilters({
      vendorName: [],
      panNumber: [],
      grnNumber: [],
      invoiceNumber: [],
      earlyPayDate: [],
      cnStatus: [],
      dnAdjusted: [],
      nakadCnReceived: [],
      anchorCnReceived: [],
      warehouseName: [],
      earlyPayPass: [],
    });
    onClose();
  };

  return (
    <Dialog open={open} sx={{ "& .MuiDialog-paper": { borderRadius: "4px", minWidth: "700px" } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px" }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Apply Filter
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Name</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={vendorNames}
                setTags={setVendorNames}
                fullWidth={true}
                placeholder="Newline separated Vendor Names"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={vendorNameOptions}
                accessor={"vendorName"}
                uniqueKeyAccessor={"vendorName"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">PAN</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={PANNumbers}
                setTags={setPANNumbers}
                fullWidth={true}
                placeholder="Newline separated PAN Numbers"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={PANOptions}
                accessor={"panNumber"}
                uniqueKeyAccessor={"panNumber"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">GRN Number</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={GRNNumbers}
                setTags={setGRNNumbers}
                fullWidth={true}
                placeholder="Newline separated GRN Numbers"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={GRNOptions}
                accessor={"grnNumber"}
                uniqueKeyAccessor={"grnNumber"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Invoice Numbers</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={invoiceNumbers}
                setTags={setInvoiceNumbers}
                fullWidth={true}
                placeholder="Newline separated Invoice Numbers"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={invoiceOptions}
                accessor={"invoiceNumber"}
                uniqueKeyAccessor={"invoiceNumber"}
              />
            </Box>
          </Box>
          {(useCase === "previousPayments" || useCase === "anchorSidePayments") && (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 16px",
                }}
              >
                <Typography variant="subtitle2">CN Status</Typography>
                <Box sx={{ width: "400px" }}>
                  <TagsInputUniqueKeys
                    tags={cnStatuses}
                    setTags={setCNStatuses}
                    fullWidth={true}
                    placeholder="Newline separated CN Status"
                    splitter="newline"
                    limitTags={1}
                    textFieldProps={{ multiline: true, maxRows: 1 }}
                    options={[
                      { cnStatus: CN_Status_Type.CN_PENDING },
                      { cnStatus: CN_Status_Type.CN_VERIFIED },
                      { cnStatus: CN_Status_Type.CN_REJECTED },
                      { cnStatus: CN_Status_Type.REVIEW_PENDING },
                    ]}
                    accessor={"cnStatus"}
                    uniqueKeyAccessor={"cnStatus"}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 16px",
                }}
              >
                <Typography variant="subtitle2">DN Adjusted</Typography>
                <Box sx={{ width: "400px" }}>
                  <TagsInputUniqueKeys
                    tags={dnAdjusted}
                    setTags={setDNAdjusted}
                    fullWidth={true}
                    placeholder="Newline separated DN Adjusted"
                    splitter="newline"
                    limitTags={1}
                    textFieldProps={{ multiline: true, maxRows: 1 }}
                    options={[{ dnAdjusted: IYesNo.YES }, { dnAdjusted: IYesNo.NO }]}
                    accessor={"dnAdjusted"}
                    uniqueKeyAccessor={"dnAdjusted"}
                  />
                </Box>
              </Box>
            </>
          )}
          {useCase === "previousPayments" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 16px",
                }}
              >
                <Typography variant="subtitle2">Actual CN received (NAKAD)</Typography>
                <Box sx={{ width: "400px" }}>
                  <TagsInputUniqueKeys
                    tags={nakadCnReceived}
                    setTags={setNakadCnReceived}
                    fullWidth={true}
                    placeholder="Newline separated NAKAD CN Received"
                    splitter="newline"
                    limitTags={1}
                    textFieldProps={{ multiline: true, maxRows: 1 }}
                    options={[{ nakadCnReceived: IYesNo.YES }, { nakadCnReceived: IYesNo.NO }]}
                    accessor={"nakadCnReceived"}
                    uniqueKeyAccessor={"nakadCnReceived"}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 16px",
                }}
              >
                <Typography variant="subtitle2">Actual CN received (Swiggy)</Typography>
                <Box sx={{ width: "400px" }}>
                  <TagsInputUniqueKeys
                    tags={anchorCnReceived}
                    setTags={setAnchorCnReceived}
                    fullWidth={true}
                    placeholder="Newline separated Actual CN received (Swiggy)"
                    splitter="newline"
                    limitTags={1}
                    textFieldProps={{ multiline: true, maxRows: 1 }}
                    options={[{ anchorCnReceived: IYesNo.YES }, { anchorCnReceived: IYesNo.NO }]}
                    accessor={"anchorCnReceived"}
                    uniqueKeyAccessor={"anchorCnReceived"}
                  />
                </Box>
              </Box>
            </>
          )}
          {useCase !== "anchorSidePayments" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 16px",
              }}
            >
              <Typography variant="subtitle2">Warehouse Name</Typography>
              <Box sx={{ width: "400px" }}>
                <TagsInputUniqueKeys
                  tags={warehouseName}
                  setTags={setWarehouseName}
                  fullWidth={true}
                  placeholder="Newline separated Warehouse Name"
                  splitter="newline"
                  limitTags={1}
                  textFieldProps={{ multiline: true, maxRows: 1 }}
                  options={warehouseOptions}
                  accessor={"warehouseName"}
                  uniqueKeyAccessor={"warehouseName"}
                />
              </Box>
            </Box>
          )}
          {useCase === "dailyPayments" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 16px",
              }}
            >
              <Typography variant="subtitle2">Early payment pass</Typography>
              <Box sx={{ width: "400px" }}>
                <TagsInputUniqueKeys
                  tags={earlyPayPass}
                  setTags={setEarlyPayPass}
                  fullWidth={true}
                  placeholder="Newline separated Early payment pass"
                  splitter="newline"
                  limitTags={1}
                  textFieldProps={{ multiline: true, maxRows: 1 }}
                  options={[{ earlyPayPass: IYesNo.YES }, { earlyPayPass: IYesNo.NO }]}
                  accessor={"earlyPayPass"}
                  uniqueKeyAccessor={"earlyPayPass"}
                />
              </Box>
            </Box>
          )}
          {useCase !== "dailyPayments" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 16px",
              }}
            >
              <Typography variant="subtitle2">Early Pay Date</Typography>
              <FormControl sx={{ width: "400px", ml: "auto", flexFlow: "row", justifyContent: "space-between" }}>
                <TextField
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  id="outlined-size-small"
                  size="small"
                  type="date"
                  value={fromEarlyPayDate || ""}
                  onChange={(e) => {
                    setFromEarlyPayDate(e.target.value);
                  }}
                />
                <TextField
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  id="outlined-size-small"
                  size="small"
                  type="date"
                  value={toEarlyPayDate || ""}
                  onChange={(e) => {
                    setToEarlyPayDate(e.target.value);
                  }}
                />
              </FormControl>
            </Box>
          )}
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <CustomButton
          onClick={() => {
            handleClearFilters();
          }}
          variant="text"
        >
          Clear Filter
        </CustomButton>
        <CustomButton
          variant="contained"
          onClick={() => {
            handleApplyFilter();
          }}
        >
          Apply
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
}
